/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  call,
  put,
  takeLatest,
  ForkEffect,
} from "redux-saga/effects";
import {
  getPolicies,
  getPoliciesSuccess,
  getPoliciesFailure,
  getPolicyDetail,
  getPolicyDetailSuccess,
  getPolicyDetailFailure,
  updatePolicyRules,
  updatePolicyRulesSuccess,
  updatePolicyRulesFailure,
  updatePolicy,
  updatePolicySuccess,
  updatePolicyFailure,
  createPolicy,
  createPolicySuccess,
  createPolicyFailure,
  deletePolicy,
  deletePolicySuccess,
  deletePolicyFailure,
} from "./slice";
import {
  InternalPolicyItem, PolicyDetailType, getInternalPolicies,
  getPolicyDetail as getPolicyDetailApi, patchPolicy as patchPolicyApi,
  postPolicy as postPolicyApi, deletePolicyApi,
} from "#/apis/infra/getPolicies";
import InternalResponse from "#/types/internalResponse";
import { resolveTimezone } from "#/infra/saga";
import { toast } from "#/infra/Toastify";
import i18n from "#/i18n";

function* handleGetPolicies(
  action: ReturnType<typeof getPolicies>,
): Generator<any, void, any> {
  try {
    const { accessToken, period, timezone } = action.payload;
    const resolvedTimezone: string = yield resolveTimezone(timezone);

    const response: InternalResponse<InternalPolicyItem[]> = yield call(getInternalPolicies, {
      period,
      timezone: resolvedTimezone,
      token: accessToken,
    });

    // eslint-disable-next-line no-nested-ternary
    const policies = response.success && response.data
      ? Array.isArray(response.data) ? response.data : [response.data]
      : [];

    yield put(getPoliciesSuccess(policies));
  } catch (error: any) {
    yield put(getPoliciesFailure(error.message || "Failed to fetch policies"));
  }
}

function* handleGetPolicyDetail(
  action: ReturnType<typeof getPolicyDetail>,
): Generator<any, void, any> {
  try {
    const { accessToken, policyId } = action.payload;

    const response: InternalResponse<PolicyDetailType> = yield call(getPolicyDetailApi, {
      policyId,
      token: accessToken,
    });

    if (response.success && response.data) {
      yield put(getPolicyDetailSuccess(response.data));
    } else {
      yield put(getPolicyDetailFailure(response.msg || "Failed to fetch policy detail"));
    }
  } catch (error: any) {
    yield put(getPolicyDetailFailure(error.message || "Failed to fetch policy detail"));
  }
}

function* handleUpdatePolicyRules(
  action: ReturnType<typeof updatePolicyRules>,
): Generator<any, void, any> {
  try {
    const { accessToken, policyId, rulesList } = action.payload;

    const response: InternalResponse<PolicyDetailType> = yield call(patchPolicyApi, {
      policyId,
      token: accessToken,
      data: { rules_list: rulesList },
    });

    if (response.success && response.data) {
      yield put(updatePolicyRulesSuccess(response.data));
      toast.success(i18n.t("toast.rulesUpdated", { ns: "policySpec" }));
    } else {
      yield put(updatePolicyRulesFailure(response.msg || "Failed to update policy rules"));
    }
  } catch (error: any) {
    yield put(updatePolicyRulesFailure(error.message || "Failed to update policy rules"));
  }
}

function* handleUpdatePolicy(
  action: ReturnType<typeof updatePolicy>,
): Generator<any, void, any> {
  try {
    const {
      accessToken, policyId, policyName, description, riskLevel, policyType, rulesList, enabled,
    } = action.payload;

    const patchData: Record<string, any> = { policy_name: policyName, description, risk_level: riskLevel };
    if (policyType) {
      patchData.policy_type = policyType;
    }
    if (rulesList) {
      patchData.rules_list = rulesList;
    }
    if (enabled != null) {
      patchData.policy_status = enabled ? "enable" : "disable";
    }

    const response: InternalResponse<PolicyDetailType> = yield call(patchPolicyApi, {
      policyId,
      token: accessToken,
      data: patchData,
    });

    if (response.success && response.data) {
      yield put(updatePolicySuccess(response.data));
      yield put(getPolicies({ accessToken, period: 7 }));
      toast.success(i18n.t("toast.policyUpdated", { ns: "policySpec" }));
    } else {
      yield put(updatePolicyFailure(response.msg || "Failed to update policy"));
    }
  } catch (error: any) {
    yield put(updatePolicyFailure(error.message || "Failed to update policy"));
  }
}

function* handleCreatePolicy(
  action: ReturnType<typeof createPolicy>,
): Generator<any, void, any> {
  try {
    const {
      accessToken, policyName, description, riskLevel, serviceTokenIds, policyType, rulesList, enabled: policyEnabled,
    } = action.payload;

    const response: InternalResponse<PolicyDetailType> = yield call(postPolicyApi, {
      token: accessToken,
      data: {
        service_token_ids: serviceTokenIds,
        policy_name: policyName,
        description,
        risk_level: riskLevel,
        policy_type: policyType,
        rules_list: rulesList ?? [],
        policy_status: policyEnabled ? "enable" : "disable",
      },
    });

    if (response.success && response.data) {
      yield put(createPolicySuccess());
      yield put(getPolicies({ accessToken, period: 7 }));
      toast.success(i18n.t("toast.policyCreated", { ns: "policySpec" }));
    } else {
      yield put(createPolicyFailure(response.msg || "Failed to create policy"));
    }
  } catch (error: any) {
    yield put(createPolicyFailure(error.message || "Failed to create policy"));
  }
}

function* handleDeletePolicy(
  action: ReturnType<typeof deletePolicy>,
): Generator<any, void, any> {
  try {
    const { accessToken, policyId, policyName } = action.payload;

    const response: InternalResponse = yield call(deletePolicyApi, {
      policyId,
      token: accessToken,
    });

    if (response.success) {
      yield put(deletePolicySuccess());
      yield put(getPolicies({ accessToken, period: 7 }));
      toast.success(i18n.t("toast.policyDeleted", { ns: "policySpec", name: policyName }));
    } else {
      yield put(deletePolicyFailure(response.msg || "Failed to delete policy"));
    }
  } catch (error: any) {
    yield put(deletePolicyFailure(error.message || "Failed to delete policy"));
  }
}

export default function* policiesSaga(): Generator<ForkEffect<never>, void, unknown> {
  yield takeLatest(getPolicies.type, handleGetPolicies);
  yield takeLatest(getPolicyDetail.type, handleGetPolicyDetail);
  yield takeLatest(updatePolicyRules.type, handleUpdatePolicyRules);
  yield takeLatest(updatePolicy.type, handleUpdatePolicy);
  yield takeLatest(createPolicy.type, handleCreatePolicy);
  yield takeLatest(deletePolicy.type, handleDeletePolicy);
}
